/* Export a scrolling DOM panel to a downloadable PDF.
 *
 * Sibling of exportFlowToPdf, which only works on a React Flow canvas — it needs getNodesBounds
 * and a .react-flow__viewport. The Readiness screens are ordinary scrolling DOM, so they need the
 * same treatment by different means. Conventions kept identical on purpose: ask for the filename
 * FIRST inside the click's user activation, render at high resolution, size the page to the
 * content so nothing is cut, and fall back to a plain download where the picker is unavailable.
 *
 * THE SCROLL CONTAINER IS THE WHOLE PROBLEM. A Readiness view is a fixed-height flex child with
 * overflow-auto, so the DOM only lays out what fits on screen — capture it untouched and the PDF
 * contains one screenful and a scrollbar. Every scrollable ancestor and descendant is therefore
 * unclamped for the duration of the capture and restored afterwards, in a finally, so an error
 * mid-render cannot leave the live screen stuck at full height.
 *
 * The page takes the element's OWN background rather than forcing white: the app has a dark theme,
 * and white behind light-on-dark text produces a page you cannot read.
 */
import { toPng } from 'html-to-image'
import { jsPDF } from 'jspdf'

/** Elements whose clipping has to be lifted, with what to put back. */
interface Saved {
  el: HTMLElement
  overflow: string
  height: string
  maxHeight: string
  position: string
}

function unclamp(root: HTMLElement): Saved[] {
  const saved: Saved[] = []
  const touch = (el: HTMLElement) => {
    saved.push({
      el,
      overflow: el.style.overflow,
      height: el.style.height,
      maxHeight: el.style.maxHeight,
      position: el.style.position,
    })
    el.style.overflow = 'visible'
    el.style.maxHeight = 'none'
    // EVERY scrollable box is expanded to its content, not just the root. Two reasons:
    //   - A cropped list with a scrollbar you cannot scroll is not an export of anything.
    //   - The Gantt's scroll area is `absolute inset-0`, so its height comes from its parent
    //     rather than its content; lifting overflow alone would leave it exactly one screenful.
    // The cost is that a long list makes a long page, which is the faithful answer.
    if (el.scrollHeight > el.clientHeight || el === root) {
      el.style.height = `${el.scrollHeight}px`
      // An absolutely-positioned box ignores height while its inset pins all four edges.
      if (getComputedStyle(el).position === 'absolute') el.style.position = 'relative'
    }
  }
  touch(root)
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
    const cs = getComputedStyle(el)
    if (/(auto|scroll)/.test(cs.overflow + cs.overflowY + cs.overflowX)) touch(el)
  }
  return saved
}

function restore(saved: Saved[]) {
  for (const s of saved) {
    s.el.style.overflow = s.overflow
    s.el.style.height = s.height
    s.el.style.maxHeight = s.maxHeight
    s.el.style.position = s.position
  }
}

export async function exportElementToPdf(opts: {
  el: HTMLElement | null
  filename: string
  /** Drawn above the capture, so a printed page says what it is without its tab. */
  title?: string
  subtitle?: string
  pixelRatio?: number
  pad?: number
}): Promise<void> {
  const { el, filename, title, subtitle, pixelRatio = 2, pad = 24 } = opts
  if (!el) return

  // Ask where to save BEFORE any long await: showSaveFilePicker must run inside the click's user
  // activation or the browser refuses it. Cancelling aborts, rather than saving somewhere else.
  let fileHandle: any = null
  const picker = (window as any).showSaveFilePicker
  if (typeof picker === 'function') {
    try {
      fileHandle = await picker({
        suggestedName: filename,
        types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
      })
    } catch (err: any) {
      if (err?.name === 'AbortError') return
      fileHandle = null
    }
  }

  const background = getComputedStyle(el).backgroundColor || '#ffffff'
  const opaque = background === 'rgba(0, 0, 0, 0)' || background === 'transparent'
    ? getComputedStyle(document.body).backgroundColor || '#ffffff'
    : background

  const saved = unclamp(el)
  let dataUrl: string
  let w: number
  let h: number
  try {
    // Read the size AFTER unclamping — that is the whole point of it.
    w = Math.max(1, Math.ceil(el.scrollWidth))
    h = Math.max(1, Math.ceil(el.scrollHeight))
    dataUrl = await toPng(el, {
      backgroundColor: opaque,
      width: w,
      height: h,
      pixelRatio,
      cacheBust: true,
      // Skip anything marked print:hidden — palettes, toggles and buttons are furniture, not report.
      filter: (node) => !(node instanceof HTMLElement && node.dataset?.pdfExclude === 'true'),
      style: { width: `${w}px`, height: `${h}px` },
    })
  } finally {
    // In a finally so a failed render cannot leave the live screen expanded to its scroll height.
    restore(saved)
  }

  const headerH = title ? 56 : 0
  const pageW = w + pad * 2
  const pageH = h + pad * 2 + headerH
  const pdf = new jsPDF({
    orientation: pageW >= pageH ? 'landscape' : 'portrait',
    unit: 'px',
    format: [pageW, pageH],
    compress: true,
  })

  if (title) {
    pdf.setFontSize(18)
    pdf.text(title, pad, pad + 20)
    if (subtitle) {
      pdf.setFontSize(10)
      pdf.setTextColor(110)
      pdf.text(subtitle, pad, pad + 38)
      pdf.setTextColor(0)
    }
  }
  pdf.addImage(dataUrl, 'PNG', pad, pad + headerH, w, h)

  if (fileHandle) {
    const writable = await fileHandle.createWritable()
    await writable.write(pdf.output('blob'))
    await writable.close()
  } else {
    pdf.save(filename)
  }
}
